import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Series: Menopause — "The Vice Principal Who Tried Everything".

export const menopauseLiz: Scenario = {
  slug: 'menopause-liz',
  series: 'Menopause',
  title: "She changed everything. One missing clue explained what helped.",
  socialHook:
    "Liz, 52, cleaned up her diet, walked every morning, and still woke soaked from night sweats. The clue was not that lifestyle failed. It was that one food-based signal could work unusually well for her, if she used the right form and dose.",
  meta: {
    description:
      "A fictional case study: Liz is a 52-year-old high school vice principal trying to manage menopause without HRT after her mother's clot on oral hormones. Her ESR1, CYP1B1/UGT1A, and VDR genetics explain why targeted phytoestrogens and vitamin D matter more for her than generic lifestyle advice.",
  },

  persona: {
    name: 'Liz',
    age: 52,
    sex: 'Female',
    ancestry: 'African-American',
    occupation: 'High school vice principal',
    chiefConcern:
      'Menopause hit at 50 — hot flashes every few hours, night sweats soaking through sheets, fatigue, and irritability that she describes as "not like me." Reluctant to start HRT after her mother had a blood clot on it. Her family doctor recommended lifestyle changes but did not say which ones.',
    familyHistory:
      'Mother: blood clot (DVT) on oral HRT — discontinued. Grandmother: menopause in early 50s, managed without hormones.',
  },

  clinicalPicture: {
    symptoms: [
      'Hot flashes — every 2–3 hours during the day, sometimes more frequently',
      'Night sweats — severe enough to require changing sheets; disrupting sleep most nights',
      'Fatigue — carrying tiredness through the day despite going to bed early',
      'Irritability and mood changes — out of character; she notices it affecting her patience at work',
    ],
    labs: [
      {
        name: 'Vitamin D (25-OH) — the standard blood test for vitamin D status',
        value: '13 ng/mL',
        refRange: '30–80 ng/mL',
        flag: 'low',
      },
    ],
    medications: ['No prescription medications — declined HRT'],
    supplements: ['None at baseline'],
    lifestyle: [
      'Diet: broadly improved over the past year — more vegetables, less processed food; felt marginally better but could not identify the driver',
      'Exercise: walking 20–30 minutes most mornings before work',
      'Non-smoker, alcohol 2–3 drinks per week',
      'Sleep: severely disrupted by night sweats — averaging 5–6 broken hours',
      'Soy intake: occasional — tofu in stir-fries once or twice a week, soy milk in coffee',
    ],
  },

  genetics: [
    {
      gene: 'ESR1',
      variant: 'High-sensitivity variant',
      rsid: 'rs2234693',
      genotype: 'Higher-responsiveness allele',
      plainMeaning:
      "Some foods send a weak estrogen-like signal, and Liz's body is unusually good at hearing it. Her estrogen receptors respond strongly enough that soy, flaxseed, and red clover may matter more for her than they do for many women.",
      technicalDetail:
        "ESR1 encodes estrogen receptor alpha, the primary receptor through which estradiol acts in the hypothalamus, breast, bone, and cardiovascular tissue. Liz's variant is associated with increased receptor responsiveness — a lower threshold for activation by estrogen ligands, including weak agonists such as phytoestrogens (isoflavones, lignans). Phytoestrogens typically bind ESR1 with roughly 1/1000th the affinity of estradiol — a signal most receptors register as background noise. In Liz, the higher-responsiveness variant amplifies that weak signal into a functionally relevant one. This is why food-based approaches that would be inert or marginal for many women are unusually viable for her specifically.",
    },
    {
      gene: 'CYP1B1 / UGT1A',
      variant: 'Slow-clearance variant',
      rsid: 'rs1056836',
      genotype: 'Reduced-activity allele',
      plainMeaning:
        "Liz does not just hear the food signal more clearly; she may keep it around longer. Most people get a brief, weak effect from soy or flax. Liz may get a steadier one.",
      technicalDetail:
        "CYP1B1 and UGT1A enzymes are responsible for hydroxylating and glucuronidating isoflavones and lignans after intestinal absorption. In fast metabolizers, this processing narrows the window of receptor activity to a few hours. Liz's reduced-activity variants extend phytoestrogen half-life substantially — the active compounds (genistein, daidzein, enterolactone) remain at receptor-activating concentrations for longer before being conjugated and cleared. Combined with her ESR1 responsiveness, this creates a compounding effect: a stronger signal lasting longer. The practical implication is that dose, frequency, and form matter more for her than generic advice implies; fermented forms (tempeh, miso) and ground sources (ground flaxseed) deliver better bioavailability than processed soy.",
    },
    {
      gene: 'VDR',
      variant: 'Reduced-function variant',
      rsid: 'rs2228570',
      genotype: 'Lower-activity allele',
      plainMeaning:
        "Her vitamin D level is low, and her receptors may respond less efficiently to the vitamin D she does have. That makes hot flashes a hormone story and a vitamin D story at the same time.",
      technicalDetail:
        "VDR encodes the vitamin D receptor, which — beyond its role in calcium metabolism — is co-expressed with estrogen receptors in the hypothalamus, the brain region that governs thermoregulation. Low vitamin D signaling can impair temperature-set-point stability and contribute to vasomotor symptoms (hot flashes). African-American women are at higher baseline risk of vitamin D deficiency because eumelanin reduces cutaneous UVB conversion of 7-dehydrocholesterol to pre-vitamin D3. Liz's VDR reduced-function variant compounds this by reducing tissue responsiveness once vitamin D is absorbed. Hot flash frequency in Liz is therefore both an estrogen story and a vitamin D story — addressing only one moves the dial less than addressing both.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She changed everything and still could not sleep",
      body: "Liz was used to solving problems with incomplete information. As a vice principal, that was part of the job. Menopause was different. Her family doctor told her to eat better, reduce stress, and move more, so she did. She cut processed food, added vegetables, and walked most mornings before work. She felt a little better, but the hot flashes still came every two or three hours and the night sweats still soaked the sheets. She had changed so many things that she could not tell what was helping. She was running a school on five broken hours of sleep.",
    },
    {
      type: 'narrative',
      heading: "Her caution made sense. The advice was too vague.",
      body: "Liz did not refuse HRT out of stubbornness. Her mother had a blood clot on oral estrogen, so trying non-hormonal options first felt reasonable. The problem was that the advice came as a blur: eat more soy, try herbs, lower stress. It was the same advice many women hear, regardless of how their bodies process those compounds. That is how Liz ended up slightly better but still stuck.",
    },
    {
      type: 'reveal',
      heading: "The food signal was real. She was using the weaker version.",
      body: "Plant estrogens — mild estrogen-like compounds in soy, flaxseed, and red clover — are usually a faint signal. Liz's ESR1 result suggests her estrogen receptors may pick up that signal more readily, while her CYP1B1 and UGT1A results suggest she may clear it more slowly. That makes a food-first approach more plausible for her than for many women. But form matters. Fermented soy such as tempeh and miso, and ground flaxseed, deliver a different bioavailability profile than occasional soy milk or processed soy. She was close to the right clue, but not using it strongly or consistently enough.",
    },
    {
      type: 'reveal',
      heading: "Her hot flashes had a second driver",
      body: "The brain's thermostat depends on more than estrogen. Liz's vitamin D level was far below the target range, and her VDR result suggests her tissues may respond less efficiently even when vitamin D is present. This is especially relevant because darker skin produces less vitamin D from the same sunlight exposure, and indoor work makes the gap wider. Her hot flashes were not only an estrogen story. They were also a vitamin D story, which explains why general lifestyle changes moved the needle only a little.",
    },
    {
      type: 'action',
      heading: "Five targeted changes — grounded in her specific biology",
      items: [
        "Switch soy sources to fermented forms: tempeh is a high-bioavailability phytoestrogen source available as a regular food. Aim for a portion three to four times a week. Miso, added to soups, dressings, or marinades, is a secondary option. These are not interchangeable with soy milk for Liz's purposes.",
        "Add ground flaxseed daily — one to two tablespoons into oatmeal, a smoothie, or yogurt. Flaxseed is the richest dietary source of lignans, a class of phytoestrogen distinct from isoflavones. The seed must be ground, not whole, for the lignans to be bioavailable.",
        "Add red clover tea — two cups a day. Red clover is high in isoflavones and is one of the better-studied food-based sources for vasomotor symptom reduction. The evidence for it is stronger than for many other herbal recommendations, and it works alongside the food changes rather than replacing them.",
        "Discuss vitamin D supplementation with her family doctor — with a retest in eight weeks. At her current level and with her VDR variant, she is likely to be a higher-dose responder. Standard supplementation at 1000 IU may not move her into the functional range; 2000–4000 IU with a follow-up level is a reasonable starting point for discussion. The goal is not a number — it is hot flash frequency.",
        "Separate the changes and give the phytoestrogen approach four to six weeks at full, targeted doses before adding or changing anything else. The marginal improvement she felt on general dietary changes was real but diffuse. The goal now is a clear signal, not more noise.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Liz's 14-day symptom log",
      goalLabel: 'Hot flash frequency, sleep quality & energy',
      caption:
        "Liz tracked three symptoms across fourteen days. Days 1–3 show her baseline on general diet changes: frequent hot flashes and poor sleep. On day 4 she added targeted phytoestrogens (tempeh, ground flaxseed, red clover tea) and began vitamin D supplementation. The chart shows the gradual shift that followed, with hot flash frequency dropping noticeably around day 9.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Hot flashes', value: 8 },
            { label: 'Sleep quality', value: 3 },
            { label: 'Energy', value: 3 },
          ],
          note: 'Baseline. General diet changes only. Four hot flashes during the day, two overnight.',
        },
        {
          day: 2,
          scores: [
            { label: 'Hot flashes', value: 8 },
            { label: 'Sleep quality', value: 3 },
            { label: 'Energy', value: 3 },
          ],
          note: 'No change. Changed sheets again at 2am.',
        },
        {
          day: 3,
          scores: [
            { label: 'Hot flashes', value: 9 },
            { label: 'Sleep quality', value: 2 },
            { label: 'Energy', value: 2 },
          ],
          note: 'Rough day. Back-to-back meetings and two hot flashes before lunch. Exhausted by 3pm.',
        },
        {
          day: 4,
          scores: [
            { label: 'Hot flashes', value: 8 },
            { label: 'Sleep quality', value: 3 },
            { label: 'Energy', value: 3 },
          ],
          note: 'Started tempeh, ground flaxseed in oatmeal, red clover tea, and vitamin D 2000 IU.',
          milestone: 'Started targeted phytoestrogens + vitamin D',
        },
        {
          day: 5,
          scores: [
            { label: 'Hot flashes', value: 7 },
            { label: 'Sleep quality', value: 4 },
            { label: 'Energy', value: 4 },
          ],
          note: 'Slept for a longer stretch before the first wake. Marginal, but noticeable.',
          milestone: 'First full night\'s sleep in weeks',
        },
        {
          day: 6,
          scores: [
            { label: 'Hot flashes', value: 7 },
            { label: 'Sleep quality', value: 4 },
            { label: 'Energy', value: 4 },
          ],
          note: 'Two teas instead of one. Hot flashes the same frequency but slightly shorter.',
        },
        {
          day: 7,
          scores: [
            { label: 'Hot flashes', value: 6 },
            { label: 'Sleep quality', value: 5 },
            { label: 'Energy', value: 5 },
          ],
          note: 'First day under a score of 7 on hot flashes. Energy enough to walk further than usual.',
        },
        {
          day: 8,
          scores: [
            { label: 'Hot flashes', value: 6 },
            { label: 'Sleep quality', value: 5 },
            { label: 'Energy', value: 5 },
          ],
          note: "Sustained. Didn't need to change sheets last night — first time in a while.",
        },
        {
          day: 9,
          scores: [
            { label: 'Hot flashes', value: 5 },
            { label: 'Sleep quality', value: 6 },
            { label: 'Energy', value: 6 },
          ],
          note: 'Noticeable shift. Three hot flashes during the day instead of five or six.',
        },
        {
          day: 10,
          scores: [
            { label: 'Hot flashes', value: 4 },
            { label: 'Sleep quality', value: 6 },
            { label: 'Energy', value: 6 },
          ],
          note: 'Two to three hot flashes today. That has not happened since before menopause started.',
          milestone: 'Hot flashes down to 2–3 per day',
        },
        {
          day: 11,
          scores: [
            { label: 'Hot flashes', value: 4 },
            { label: 'Sleep quality', value: 7 },
            { label: 'Energy', value: 7 },
          ],
          note: 'Slept six and a half hours without waking. I forgot what that felt like.',
        },
        {
          day: 12,
          scores: [
            { label: 'Hot flashes', value: 3 },
            { label: 'Sleep quality', value: 7 },
            { label: 'Energy', value: 7 },
          ],
          note: 'Good day. Carried through a full afternoon without the usual 3pm crash.',
        },
        {
          day: 13,
          scores: [
            { label: 'Hot flashes', value: 4 },
            { label: 'Sleep quality', value: 7 },
            { label: 'Energy', value: 7 },
          ],
          note: 'Slight uptick in hot flashes — stressful parent meeting. Still far better than week one.',
        },
        {
          day: 14,
          scores: [
            { label: 'Hot flashes', value: 3 },
            { label: 'Sleep quality', value: 8 },
            { label: 'Energy', value: 8 },
          ],
          note: "Two weeks. Two to three hot flashes. Slept properly. I thought this just wasn't going to be possible for me without hormones. My body was already built for this — I just wasn't feeding it the right things.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'menopause',
    label: 'Explore your menopause genetics in Coach',
  },
}
